'use client'

import dayjs from 'dayjs'
import React from 'react'

import { Button } from '../../components/Button'
import { CheckboxField } from '../../components/CheckboxField'
import { Link } from '../../components/Link'
import { Loading } from '../../components/Loading'
import {
  Modal,
  ModalProps,
  ModalProvider,
  useModal,
} from '../../components/Modal'
import { Typography } from '../../components/Typography'
import { Document, ErrorOutline } from '../../icons'
import { formatNumber } from '../../utils/formatNumber'
import { FacilityAgreementModal } from './FacilityAgreementModal'
import { Container, OverlapList, OverlapListItem, PointInfo } from './styles'

export interface BaseReservationModalProps extends ModalProps {
  isLoading: false

  title?: string
  facilityName: string
  programName: string
  reservationDate: string
  cancelDeadline: string
  isFavorited?: boolean

  /**
   * Arrival time before the program start time in minutes
   */
  minuteAgo: number

  /**
   * Points owned by the user
   */
  userPoints: number

  /**
   * Points required to reserve the program
   */
  timetablePoints: number

  /**
   * User has read the facility agreement
   */
  hasReadAgreement?: boolean

  /**
   * Facility agreement
   */
  facilityAgreement?: {
    content: string
    createdAt: string
  }

  /**
   * Maximum number of reservations that can be made by the user.
   * null の場合は無制限（同時予約上限なし）を表す。
   */
  userReservationLimit: number | null

  /**
   * Number of current reservations made by the user
   */
  userCurrentReservationCount: number

  /**
   * Reservations that overlap with the current reservation
   */
  overlappingReservations?: {
    id: number
    point: number
    program: {
      id: number
      name: string
      duration: number
    }
    facility: {
      id: number
      name: string
    }
    timetable: {
      id: number
      startAt: string
    }
  }[]

  programLink: {
    href: string
    external?: boolean
    target?: string
  }

  canPurchaseShortage: boolean

  pointPurchaseLink: {
    href: string
    external?: boolean
    target?: string
  }

  shortagePointPurchaseLink: {
    href: string
    external?: boolean
    target?: string
  }

  privacyPolicyLink: {
    href: string
    external?: boolean
    target?: string
  }

  termsLink: {
    href: string
    external?: boolean
    target?: string
  }

  aboutPointsLink: {
    href: string
    external?: boolean
    target?: string
  }

  submitLabel?: string
  onSubmit: (args: { favoriteFacility: boolean }) => void | Promise<void>
}

export interface LoadingReservationModalProps extends ModalProps {
  isLoading: true
}

export type ReservationModalProps =
  | BaseReservationModalProps
  | LoadingReservationModalProps

export const ReservationModalInner = ({
  title = '内容確認',
  ...props
}: ReservationModalProps) => {
  const [favoriteFacility, setFavoriteFacility] = React.useState(true)
  const [isSubmitting, setSubmitting] = React.useState(false)
  const { openModal } = useModal()

  if (props.isLoading) {
    return (
      <Modal title={title} {...props}>
        <Modal.Body>
          <Container>
            <Loading size="md" />
          </Container>
        </Modal.Body>
      </Modal>
    )
  }

  const {
    facilityName,
    programName,
    reservationDate,
    cancelDeadline,
    minuteAgo,
    userPoints,
    timetablePoints,
    hasReadAgreement = false,
    facilityAgreement,
    userReservationLimit,
    userCurrentReservationCount,
    isFavorited,
    overlappingReservations = [],
    programLink,
    canPurchaseShortage = false,
    pointPurchaseLink,
    shortagePointPurchaseLink,
    privacyPolicyLink,
    termsLink,
    aboutPointsLink,
    submitLabel = '上記に同意して予約',
    onSubmit,
    ...modalProps
  } = props

  // Shortage point to reserve the program
  const shortagePoint =
    userPoints - timetablePoints < 0 ? userPoints - timetablePoints : 0

  // Whether the user has enough points to reserve the program
  const isPointShort = shortagePoint < 0

  // Points left after reserving the program
  const pointLeft =
    userPoints - timetablePoints > 0 ? userPoints - timetablePoints : 0

  // Whether the user has exceeded the maximum number of reservations.
  // userReservationLimit が null の場合は無制限のため上限超過は発生しない。
  const isExceededReservationLimit =
    userReservationLimit !== null &&
    userCurrentReservationCount >= userReservationLimit

  // Whether the user can reserve the program
  const isReservable = !isPointShort && !isExceededReservationLimit

  const hasAgreement = !hasReadAgreement && Boolean(facilityAgreement)

  const handleSubmit = async (close?: () => void) => {
    await onSubmit({ favoriteFacility })
    close?.()
    modalProps.onClose?.()
  }

  return (
    <Modal title={title} {...modalProps}>
      <Modal.Body>
        <Container>
          <div className="flex flex-col gap-1 rounded-md bg-gray-05 p-4">
            <Typography variant="subhead1" color="gray.01" textAlign="center">
              {dayjs(reservationDate).format('M月D日 HH:mm')}
            </Typography>
            <Typography variant="headline3" color="gray.01" textAlign="center">
              {programName}
            </Typography>
            <Typography variant="body2" color="gray.01" textAlign="center">
              (
              {dayjs(reservationDate)
                .subtract(minuteAgo, 'minutes')
                .format('HH:mm')}
              までに来店)
            </Typography>
            <div className="flex justify-end">
              <Link variant="secondary" {...programLink}>
                詳細を見る
              </Link>
            </div>
          </div>

          <PointInfo>
            <div className="flex justify-between">
              <Typography variant="body2" color="gray.01">
                プログラムの小計
              </Typography>
              <Typography variant="subhead2" color="gray.01">
                {formatNumber(timetablePoints)}pt
              </Typography>
            </div>

            <div className="flex justify-between">
              <Typography variant="body2" color="gray.01">
                利用可能ポイント
              </Typography>
              <Typography variant="subhead2" color="gray.01">
                {formatNumber(userPoints)}pt
              </Typography>
            </div>

            <div className="flex justify-between">
              <Typography variant="body2" color="gray.01">
                不足ポイント
              </Typography>
              <Typography variant="subhead2" color="red.01">
                {formatNumber(shortagePoint)}pt
              </Typography>
            </div>

            {!isPointShort && (
              <div className="flex justify-between">
                <Typography variant="body2" color="gray.01">
                  ご予約後のポイント残高
                </Typography>
                <Typography variant="subhead2" color="gray.01">
                  {formatNumber(pointLeft)}pt
                </Typography>
              </div>
            )}
          </PointInfo>

          <div className="mt-6 flex justify-center">
            <Link {...aboutPointsLink} variant="primary">
              保有ポイントと利用可能ポイントについて
            </Link>
          </div>

          <div className="mt-6 flex justify-between">
            <Typography variant="body1" color="gray.01">
              キャンセル期限
            </Typography>
            <Typography variant="body1" color="red.01">
              {dayjs(cancelDeadline).format('YYYY年M月D日 HH:mm')}
            </Typography>
          </div>

          <ul className="mt-2 flex flex-col gap-1">
            <Typography as="li" variant="caption1">
              ※上記時間を過ぎてからのキャンセル・ご予約時に消化されたGYYMポイントの返還はできません。
            </Typography>
            {userReservationLimit !== null && (
              <Typography as="li" variant="caption1">
                ※同時に予約できる件数は{userReservationLimit}
                件までとなりますのでご注意ください。
              </Typography>
            )}
          </ul>

          {isPointShort && canPurchaseShortage && (
            <div className="mt-6 flex flex-col items-center justify-center rounded-md bg-offWhite p-4">
              <Typography as="div" color="red.01">
                <div className="flex items-center gap-2">
                  <ErrorOutline />
                  <Typography variant="subhead1" color="gray.01">
                    不足ポイント
                  </Typography>
                  <Typography variant="subhead1">
                    {formatNumber(shortagePoint)}pt
                  </Typography>
                </div>
              </Typography>
              <Button className="mt-4" variant="brand" asChild>
                <Link {...shortagePointPurchaseLink}>ポイント購入</Link>
              </Button>
            </div>
          )}

          {isPointShort && !canPurchaseShortage && (
            <div className="mt-6 flex flex-col items-center justify-center rounded-md bg-offWhite p-4">
              <Typography as="div" color="red.01">
                <div className="flex items-center gap-2">
                  <ErrorOutline />
                  <Typography variant="subhead1" color="gray.01">
                    ポイントが不足しています
                  </Typography>
                </div>
              </Typography>
              <Button className="mt-4" variant="brand" asChild>
                <Link {...pointPurchaseLink}>ポイント購入</Link>
              </Button>
            </div>
          )}

          {overlappingReservations.length > 0 && (
            <div className="mt-6 flex flex-col items-center gap-4 rounded-md border border-red-01 p-4 text-red-01">
              <div className="flex items-center gap-2">
                <ErrorOutline />
                <Typography variant="subhead1">
                  時間帯が重複するご予約があります。
                </Typography>
              </div>
              <OverlapList>
                {overlappingReservations.map((reservation) => (
                  <OverlapListItem key={reservation.id}>
                    <Typography variant="body2" color="gray.01">
                      {dayjs(reservation.timetable.startAt).format(
                        'M月D日 HH:mm',
                      )}{' '}
                      ({reservation.program.duration}min)
                    </Typography>
                    <Typography variant="subhead2" color="gray.01">
                      {reservation.program.name}
                    </Typography>
                  </OverlapListItem>
                ))}
              </OverlapList>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-1 px-6">
            {!isFavorited && (
              <CheckboxField
                label={`${facilityName} をお気に入り登録する`}
                checked={favoriteFacility}
                onChange={(e) => {
                  setFavoriteFacility(e.target.checked)
                }}
              />
            )}

            <Link {...privacyPolicyLink} variant="secondary">
              <div className="flex items-center gap-2">
                <Document />
                プライバシーポリシー
              </div>
            </Link>

            <Link {...termsLink} variant="secondary">
              <div className="flex items-center gap-2">
                <Document />
                利用規約
              </div>
            </Link>
          </div>
        </Container>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="brand"
          size="lg"
          loading={isSubmitting}
          onClick={async () => {
            if (!hasAgreement) {
              setSubmitting(true)
              await handleSubmit()
              setSubmitting(false)
            } else {
              openModal(FacilityAgreementModal, {
                expandOnSmallDevices: true,
                facilityName,
                content: facilityAgreement?.content ?? '',
                date: facilityAgreement?.createdAt ?? '',
                onSubmit: handleSubmit,
                submitLabel,
              })
            }
          }}
          disabled={!isReservable}
          fullWidth
        >
          {isExceededReservationLimit
            ? '予約上限に達しています'
            : hasAgreement
            ? '上記に同意して次へ'
            : submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export const ReservationModal = (props: ReservationModalProps) => (
  // Wrap with modal provider for the FacilityAgreementModal
  <ModalProvider>
    <ReservationModalInner {...props} />
  </ModalProvider>
)
